import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = { class: "accordion-header-content" }
const _hoisted_3 = { class: "accordion-left-section" }
const _hoisted_4 = { class: "accordion-main-info" }
const _hoisted_5 = { class: "accordion-title-row" }
const _hoisted_6 = { class: "accordion-title" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = {
  key: 0,
  class: "accordion-subtitle"
}
const _hoisted_9 = {
  key: 1,
  class: "timestamp-performer"
}
const _hoisted_10 = { class: "accordion-right-section" }
const _hoisted_11 = { class: "accordion-timestamp" }
const _hoisted_12 = { class: "timestamp-date" }
const _hoisted_13 = { class: "timestamp-time" }
const _hoisted_14 = {
  key: 0,
  class: "accordion-content"
}
const _hoisted_15 = {
  key: 0,
  class: "no-recording-section"
}
const _hoisted_16 = { class: "no-recording-message" }
const _hoisted_17 = {
  key: 0,
  class: "recording-section"
}
const _hoisted_18 = { class: "recording-section__header" }
const _hoisted_19 = { class: "recording-download-wrapper" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = {
  key: 1,
  class: "no-recording-section"
}
const _hoisted_22 = { class: "no-recording-message" }
const _hoisted_23 = {
  key: 2,
  class: "no-recording-section"
}
const _hoisted_24 = { class: "no-recording-message processing" }
const _hoisted_25 = {
  key: 3,
  class: "no-recording-section"
}
const _hoisted_26 = { class: "no-recording-message" }
const _hoisted_27 = {
  key: 4,
  class: "loading-state"
}
const _hoisted_28 = {
  key: 5,
  class: "error-state"
}
const _hoisted_29 = { class: "error-message" }

import { AudioPlayer, LoadingBars } from "@/components/ui";
import { useCallLogBySid } from "@/composables/calls";
import { useCallDispositions } from "@/composables/calls/useCallDispositions";
import { useLogCallLogDetails } from "@/composables/calls/useCallLog";
import { useToast } from "@/composables/ui/useToast";
import type { CallLogResponse } from "@/dtos/CallLog.dto";
import CallLogService from "@/services/call-log";
import { getSocket } from "@/utils/socket";
import { joinRoom, leaveRoom } from "@/utils/socket-rooms";
import { Icon } from "@iconify/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import CallAnalysisContent from "./CallAnalysisContent.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'CallAnalysisAccordion',
  props: {
    callSid: {},
    activityLog: {},
    isExpanded: { type: Boolean },
    performedBy: {},
    recordingSid: {},
    recordingUrl: {},
    callStatus: {},
    loggedDate: {},
    loggedTime: {}
  },
  emits: ["expand", "collapse", "switch-channel"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const queryClient = useQueryClient();
const account = computed(
  () => store.getters.account.data as { organizationId?: string } | undefined
);
const organizationId = computed(() => account.value?.organizationId);

const callSidRef = computed(() => props.callSid);
const isExpandedAndReady = computed(
  () => !!props.isExpanded && !!props.callSid && !!account.value?.organizationId
);

const {
  data: queryData,
  isPending,
  isFetching,
  error: queryError,
  refetch,
} = useCallLogBySid(callSidRef, organizationId, { enabled: isExpandedAndReady });

const isLoadingCallData = computed(() => isPending.value && isFetching.value);
const isDownloading = ref(false);

// Disposition (for unanswered calls)
const $toast = useToast();
const { data: callDispositionsData } = useCallDispositions();
const callDispositions = computed(() => (callDispositionsData.value as any[]) || []);

const localDispositionId = ref<string | null>(null);
const localDispositionName = ref<string | null>(null);
const isUpdatingDisposition = ref(false);
const showDispositionDropdown = ref(false);
const dispositionAnchorRef = ref<HTMLElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});

const { mutateAsync: logCallLogDetails } = useLogCallLogDetails();

onMounted(() => {
  localDispositionId.value =
    (props.activityLog?.callLog?.callDispositionId as string | null) ?? null;
  localDispositionName.value =
    (props.activityLog?.callLog?.callDisposition?.name as string | null) ?? null;
});

// Falls back to list lookup when relation wasn't eager-loaded (fallback code path)
const displayDispositionName = computed(() => {
  if (localDispositionName.value) return localDispositionName.value;
  if (localDispositionId.value && callDispositions.value.length > 0) {
    return (
      (callDispositions.value.find((d: any) => d.id === localDispositionId.value) as any)?.name ??
      null
    );
  }
  return null;
});

const getRecordingDownloadUrl = () => {
  const token = store.getters.sessionToken;
  const sid = props.recordingSid ?? callData.value?.recordingSid;
  if (!token || !sid) return null;
  const base = `${
    process.env.NODE_ENV === "development"
      ? process.env.VUE_APP_LOCAL_SERVER_URL
      : process.env.VUE_APP_SERVER_URL
  }/${process.env.VUE_APP_VERSION}`;
  return `${base}/twilio/play-recording/${sid}?token=${encodeURIComponent(token)}`;
};

const downloadRecording = async () => {
  const url = getRecordingDownloadUrl();
  if (!url) return;
  isDownloading.value = true;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `call-recording-${props.callSid || "unknown"}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Failed to download recording:", err);
  } finally {
    isDownloading.value = false;
  }
};

const socketOverrideRef = ref<CallLogResponse | null>(null);
const parentCallSid = ref<string | null>(null);
const joinRejected = ref(false);

let socket: ReturnType<typeof getSocket> | null = null;
let listenersAttached = false;
let joinedCallRoom = false;
let reconnectHandler: (() => void) | null = null;

const unansweredStatuses = new Set(["no-answer", "busy", "failed", "rejected", "canceled"]);
const isUnansweredCall = computed(
  () => !!props.callStatus && unansweredStatuses.has(props.callStatus)
);
const unansweredTitle = computed(() => {
  const titleMap: Record<string, string> = {
    "no-answer": "Call — No Answer",
    busy: "Call — Busy",
    failed: "Call — Failed",
    rejected: "Call — Rejected",
    canceled: "Call — Canceled",
  };
  return props.callStatus
    ? titleMap[props.callStatus] || "Call — Not Connected"
    : "Call — Not Connected";
});

const callData = computed(
  () =>
    socketOverrideRef.value ??
    queryData.value ??
    (props.activityLog?.callLog as CallLogResponse | undefined) ??
    null
);

const error = computed(() => {
  if (queryError.value) {
    const e = queryError.value;
    return e instanceof Error ? e.message : "Failed to load call analysis. Please try again.";
  }
  if (!isLoadingCallData.value && !queryData.value && props.isExpanded && props.callSid) {
    return "No call data found for this call ID";
  }
  return null;
});

const awaitingRecording = computed(() => {
  const data = callData.value || props.activityLog?.callLog;
  if (!data) return false;
  if (data.recordingSid) return false;
  if (data.saveRecording === false) return false;
  if (data.status !== "completed") return false;
  if (!data.duration || data.duration <= 0) return false;
  return true;
});

function toggleDispositionDropdown() {
  if (showDispositionDropdown.value) {
    showDispositionDropdown.value = false;
    return;
  }
  if (dispositionAnchorRef.value) {
    const rect = dispositionAnchorRef.value.getBoundingClientRect();
    const showAbove = window.innerHeight - rect.bottom < 220;
    dropdownStyle.value = {
      position: "fixed",
      left: `${rect.left}px`,
      minWidth: `${Math.max(rect.width, 160)}px`,
      zIndex: "10070",
      ...(showAbove
        ? { bottom: `${window.innerHeight - rect.top + 4}px` }
        : { top: `${rect.bottom + 4}px` }),
    };
  }
  showDispositionDropdown.value = true;
}

function closeDispositionDropdown() {
  showDispositionDropdown.value = false;
}

async function selectDisposition(disp: { id: string; name: string }) {
  showDispositionDropdown.value = false;
  if (!props.callSid || localDispositionId.value === disp.id) return;
  isUpdatingDisposition.value = true;
  try {
    await logCallLogDetails({
      callSid: (props.activityLog?.callLog?.parentCallSid as string | undefined) || props.callSid,
      callDispositionId: disp.id,
    });
    localDispositionId.value = disp.id;
    localDispositionName.value = disp.name;
    queryClient.invalidateQueries({ queryKey: ["activityLogs"] });
    $toast.success("Disposition updated");
  } catch {
    $toast.error("Failed to update disposition");
  } finally {
    isUpdatingDisposition.value = false;
  }
}

function formatStatus(status: string | null) {
  if (!status) return "";
  return status
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getStatusClass(status: string) {
  const statusMap: Record<string, string> = {
    completed: "status-success",
    "no-answer": "status-warning",
    initiated: "status-secondary",
    rejected: "status-danger",
    busy: "status-warning",
    failed: "status-danger",
  };
  return statusMap[status] || "status-secondary";
}

const hasCallDataInActivityLog = computed(
  () =>
    props.activityLog?.callLog &&
    (props.activityLog.callLog.analysis ||
      props.activityLog.callLog.analysisStatus ||
      props.activityLog.callLog.transcriptUrl ||
      props.activityLog.callLog.analysisError)
);

function isProcessing(data: CallLogResponse | null | undefined) {
  if (!data) return false;
  const s = data.analysisStatus;
  const ts = data.transcriptStatus;
  return s === "pending" || s === "in_progress" || ts === "pending" || ts === "in_progress";
}

function joinCallRoom(targetCallSid: string) {
  if (!socket || !targetCallSid) return;
  if (joinedCallRoom) return;
  const current = callData.value;
  const parentSid =
    props.activityLog?.callLog?.parentCallSid ?? current?.parentCallSid ?? targetCallSid;
  const roomName = `call:${parentSid}`;
  joinRoom(socket, roomName, (ack) => {
    if (!ack.ok) joinRejected.value = true;
  });
  joinedCallRoom = true;
  parentCallSid.value = parentSid;
}

function leaveCallRoom() {
  if (!socket || !joinedCallRoom) return;
  const roomToLeave = parentCallSid.value ?? props.callSid;
  if (roomToLeave) leaveRoom(socket, `call:${roomToLeave}`);
  joinedCallRoom = false;
}

function setupSocketListeners(shouldJoinRoom = true) {
  if (listenersAttached || !props.callSid) return;
  try {
    socket = getSocket();
    if (!socket) return;

    const current = callData.value;
    const currentStatus = current?.analysisStatus ?? props.activityLog?.callLog?.analysisStatus;
    const currentTranscriptStatus =
      current?.transcriptStatus ?? props.activityLog?.callLog?.transcriptStatus;
    const processing = isProcessing(current ?? props.activityLog?.callLog);

    if (!processing && (currentStatus === "completed" || currentStatus === "failed")) return;

    listenersAttached = true;

    if (shouldJoinRoom && processing) {
      if (socket.connected && props.callSid) {
        joinCallRoom(props.callSid);
      } else {
        reconnectHandler = () => {
          if (props.callSid && processing) joinCallRoom(props.callSid);
        };
        socket.on("connect", reconnectHandler);
      }
    }

    socket.on("call:analysis:start", (data: any) => {
      const cur = callData.value;
      const callSidMatches =
        data.callSid === props.callSid ||
        data.callSid === parentCallSid.value ||
        data.callSid === cur?.callSid ||
        data.parentCallSid === props.callSid ||
        data.parentCallSid === parentCallSid.value ||
        data.parentCallSid === cur?.parentCallSid;

      if (callSidMatches) {
        if (data.parentCallSid) parentCallSid.value = data.parentCallSid;
        else if (data.callSid && !parentCallSid.value) parentCallSid.value = data.callSid;
        if (cur && cur.analysisStatus !== "completed") {
          socketOverrideRef.value = { ...cur, analysisStatus: "in_progress" };
        }
      }
    });

    socket.on("call:analysis:complete", (data: any) => {
      const cur = callData.value;
      const callSidMatches =
        data.callSid === props.callSid ||
        data.callSid === parentCallSid.value ||
        data.callSid === cur?.callSid ||
        data.parentCallSid === props.callSid ||
        data.parentCallSid === parentCallSid.value ||
        data.parentCallSid === cur?.parentCallSid;

      if (callSidMatches) {
        if (data.parentCallSid) parentCallSid.value = data.parentCallSid;
        else if (data.callSid && !parentCallSid.value) parentCallSid.value = data.callSid;

        const nextCallData: CallLogResponse = {
          ...(cur || ({} as CallLogResponse)),
          callSid: props.callSid || data.callSid,
          parentCallSid:
            data.parentCallSid || parentCallSid.value || cur?.parentCallSid || undefined,
          analysisStatus: "completed",
          analysis: {
            id: data.analysis?.id || "",
            callLogId: props.callSid || data.callSid,
            summary: data.analysis?.summary || "",
            actionItems: data.analysis?.actionItems ?? null,
            topics: data.analysis?.topics ?? null,
            sentiment: data.analysis?.sentiment ?? null,
            modelName: data.analysis?.modelName ?? null,
            callMetadata: data.analysis?.callMetadata ?? null,
            executiveSummary: data.analysis?.executiveSummary ?? null,
            detectiveAnalysis: data.analysis?.detectiveAnalysis ?? null,
            followUpStrategy: data.analysis?.followUpStrategy ?? null,
            emailRequestedInCall: data.analysis?.emailRequestedInCall ?? false,
            followUpEmail: data.analysis?.followUpEmail ?? null,
            createdAt: data.analysis?.createdAt || new Date(),
          },
          isLiveConversation: data.analysis?.isLiveConversation ?? cur?.isLiveConversation ?? false,
          isConnectedConversation:
            data.analysis?.isConnectedConversation ?? cur?.isConnectedConversation ?? false,
          connectReason: data.analysis?.connectReason ?? cur?.connectReason ?? null,
          transcriptUrl: data.transcriptUrl || cur?.transcriptUrl || null,
          recordingUrl: data.recordingUrl || cur?.recordingUrl || null,
        } as CallLogResponse;
        socketOverrideRef.value = nextCallData;
        queryClient.invalidateQueries({
          queryKey: ["callLog", props.callSid, organizationId.value],
        });
      }
    });

    socket.on("call:recording:available", (data: any) => {
      const cur = callData.value;
      const matches =
        data.callSid === props.callSid ||
        data.callSid === parentCallSid.value ||
        data.callSid === cur?.parentCallSid ||
        data.callSid === cur?.callSid;
      if (matches) {
        if (cur) {
          socketOverrideRef.value = {
            ...cur,
            recordingSid: data.recordingSid,
            recordingUrl: data.recordingUrl,
          };
        }
        queryClient.invalidateQueries({
          queryKey: ["callLog", props.callSid, organizationId.value],
        });
      }
    });
  } catch (err) {
    console.error("Failed to set up Socket.IO listeners for accordion:", err);
  }
}

function cleanupSocketListeners() {
  if (socket && listenersAttached) {
    try {
      socket.off("call:analysis:start");
      socket.off("call:analysis:complete");
      socket.off("call:recording:available");
      if (reconnectHandler) {
        socket.off("connect", reconnectHandler);
        reconnectHandler = null;
      }
      listenersAttached = false;
    } catch (err) {
      console.error("Error cleaning up accordion listeners:", err);
    }
  }
  leaveCallRoom();
}

function toggleAccordion() {
  if (!props.callSid) return;

  if (props.isExpanded) {
    emit("collapse");
    cleanupSocketListeners();
  } else {
    emit("expand");
    if (hasCallDataInActivityLog.value) {
      const status = props.activityLog!.callLog.analysisStatus;
      const transcriptStatus = props.activityLog!.callLog.transcriptStatus;
      if (
        transcriptStatus === "pending" ||
        transcriptStatus === "in_progress" ||
        status === "pending" ||
        status === "in_progress"
      ) {
        setupSocketListeners(true);
      } else if (status === "failed" || props.activityLog!.callLog.analysisError) {
        socketOverrideRef.value = null;
      }
    }
  }
}

watch(queryData, (newData) => {
  if (newData) socketOverrideRef.value = null;
});

watch(
  () => [props.recordingSid, props.recordingUrl],
  ([newSid, newUrl]) => {
    if (newSid && !newUrl && !callData.value?.recordingUrl) {
      refetch();
    }
  },
  { immediate: true }
);

watch(
  () => props.isExpanded,
  (newVal) => {
    if (!newVal) cleanupSocketListeners();
  }
);

watch(
  () => [queryData.value, props.isExpanded],
  ([data, expanded]) => {
    if (expanded && isProcessing(data as CallLogResponse | undefined)) {
      setupSocketListeners(true);
    }
  }
);

watch(showDispositionDropdown, (open) => {
  if (open) {
    document.addEventListener("click", closeDispositionDropdown);
  } else {
    document.removeEventListener("click", closeDispositionDropdown);
  }
});

function handleFollowUpEmail(payload: { subject: string; content: string }) {
  emit("switch-channel", { channel: "email", subject: payload.subject, content: payload.content });
}

async function handleRegenerate() {
  const cur = callData.value;
  if (!cur?.id) return;

  try {
    // Show in-progress state immediately
    socketOverrideRef.value = { ...(cur as CallLogResponse), analysisStatus: "in_progress" };
    // Reset listeners so setupSocketListeners can re-attach and join the call room
    cleanupSocketListeners();
    setupSocketListeners(true);
    await CallLogService.regenerateAnalysis(cur.id);
  } catch (err) {
    console.error("Failed to trigger regeneration:", err);
    socketOverrideRef.value = { ...(cur as CallLogResponse), analysisStatus: "failed" };
  }
}

// re-fetch CallLog so backend mints a fresh presigned transcriptUrl
function handleRefreshTranscript() {
  if (!props.callSid) return;
  queryClient.invalidateQueries({
    queryKey: ["callLog", props.callSid, organizationId.value],
  });
}

onBeforeUnmount(() => {
  cleanupSocketListeners();
  document.removeEventListener("click", closeDispositionDropdown);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["call-analysis-accordion", { 'call-analysis-accordion--flat': isUnansweredCall.value }])
  }, [
    _createElementVNode("button", {
      type: "button",
      class: _normalizeClass(["accordion-header", { 'is-expanded': __props.isExpanded, 'accordion-header--flat': isUnansweredCall.value }]),
      onClick: _cache[4] || (_cache[4] = ($event: any) => (isUnansweredCall.value ? undefined : toggleAccordion())),
      disabled: !__props.callSid
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_unref(Icon), {
            icon: "mdi:phone-outline",
            width: "20",
            height: "20",
            class: _normalizeClass(["phone-icon", { 'phone-icon--muted': isUnansweredCall.value }])
          }, null, 8, ["class"]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(isUnansweredCall.value ? unansweredTitle.value : "Call has been made"), 1),
              (__props.callStatus)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass(["accordion-status-badge", getStatusClass(__props.callStatus)])
                  }, _toDisplayString(formatStatus(__props.callStatus)), 3))
                : _createCommentVNode("", true),
              (__props.callSid)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    ref_key: "dispositionAnchorRef",
                    ref: dispositionAnchorRef,
                    class: "disp-chip-wrapper",
                    onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                  }, [
                    _createElementVNode("div", {
                      role: "button",
                      tabindex: "0",
                      class: _normalizeClass(["disp-chip", {
                    'disp-chip--set': displayDispositionName.value,
                    'disp-chip--disabled': isUpdatingDisposition.value,
                  }]),
                      onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (isUpdatingDisposition.value ? undefined : toggleDispositionDropdown()), ["stop"])),
                      onKeydown: [
                        _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (
                    isUpdatingDisposition.value ? undefined : toggleDispositionDropdown()
                  ), ["stop","prevent"]), ["enter"])),
                        _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (
                    isUpdatingDisposition.value ? undefined : toggleDispositionDropdown()
                  ), ["stop","prevent"]), ["space"]))
                      ]
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:tag-outline",
                        width: "13",
                        height: "13"
                      }),
                      _createElementVNode("span", null, _toDisplayString(isUpdatingDisposition.value
                      ? "Updating..."
                      : displayDispositionName.value || "Set Disposition"), 1),
                      _createVNode(_unref(Icon), {
                        icon: "mdi:chevron-down",
                        width: "13",
                        height: "13"
                      })
                    ], 34),
                    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                      (showDispositionDropdown.value)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "disp-dropdown",
                            style: _normalizeStyle(dropdownStyle.value)
                          }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(callDispositions.value, (disp) => {
                              return (_openBlock(), _createElementBlock("button", {
                                key: disp.id,
                                type: "button",
                                class: _normalizeClass(["disp-dropdown__item", { 'disp-dropdown__item--active': localDispositionId.value === disp.id }]),
                                onClick: _withModifiers(($event: any) => (selectDisposition(disp)), ["stop"])
                              }, _toDisplayString(disp.name), 11, _hoisted_7))
                            }), 128))
                          ], 4))
                        : _createCommentVNode("", true)
                    ]))
                  ], 512))
                : _createCommentVNode("", true)
            ]),
            (!isUnansweredCall.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, "Call Details & Analysis"))
              : _createCommentVNode("", true),
            (__props.performedBy)
              ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:account-outline",
                    width: "11",
                    height: "11"
                  }),
                  _createTextVNode(" " + _toDisplayString(__props.performedBy), 1)
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("span", _hoisted_12, _toDisplayString(__props.loggedDate), 1),
            _createElementVNode("span", _hoisted_13, _toDisplayString(__props.loggedTime), 1)
          ]),
          (!isUnansweredCall.value)
            ? (_openBlock(), _createBlock(_unref(Icon), {
                key: 0,
                icon: __props.isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down',
                width: "22",
                height: "22",
                class: "accordion-icon"
              }, null, 8, ["icon"]))
            : _createCommentVNode("", true)
        ])
      ])
    ], 10, _hoisted_1),
    (__props.isExpanded && !isUnansweredCall.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          (joinRejected.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:lock-outline",
                    width: "18",
                    height: "18"
                  }),
                  _cache[7] || (_cache[7] = _createElementVNode("span", null, "Not authorized to view this call", -1))
                ])
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (__props.recordingUrl || callData.value?.recordingUrl)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                      _createElementVNode("div", _hoisted_18, [
                        _cache[9] || (_cache[9] = _createElementVNode("h6", { class: "section-title" }, "Call Recording", -1)),
                        _createElementVNode("span", _hoisted_19, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "recording-download-btn",
                            disabled: isDownloading.value,
                            onClick: _withModifiers(downloadRecording, ["stop"])
                          }, [
                            (!isDownloading.value)
                              ? (_openBlock(), _createBlock(_unref(Icon), {
                                  key: 0,
                                  icon: "material-symbols:download-rounded",
                                  width: "16",
                                  height: "16"
                                }))
                              : (_openBlock(), _createBlock(_unref(LoadingBars), {
                                  key: 1,
                                  size: "sm",
                                  showText: false
                                }))
                          ], 8, _hoisted_20),
                          _cache[8] || (_cache[8] = _createElementVNode("span", { class: "recording-download-tooltip" }, "Download recording", -1))
                        ])
                      ]),
                      _createElementVNode("div", {
                        class: "audio-player-wrapper",
                        onClick: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        _createVNode(_unref(AudioPlayer), {
                          "recording-sid": __props.recordingSid ?? callData.value?.recordingSid ?? undefined,
                          "recording-url": __props.recordingUrl ?? callData.value?.recordingUrl ?? undefined,
                          duration: callData.value?.duration,
                          compact: true
                        }, null, 8, ["recording-sid", "recording-url", "duration"])
                      ])
                    ]))
                  : (__props.callSid && (__props.recordingSid || callData.value?.recordingSid) && __props.activityLog?.callLog)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                        _createElementVNode("div", _hoisted_22, [
                          _createVNode(_unref(Icon), {
                            icon: "mdi:information-outline",
                            width: "18",
                            height: "18"
                          }),
                          _cache[10] || (_cache[10] = _createElementVNode("span", null, "Processing recording...", -1))
                        ])
                      ]))
                    : (__props.callSid && awaitingRecording.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                          _createElementVNode("div", _hoisted_24, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:loading",
                              width: "18",
                              height: "18",
                              class: "spin-icon"
                            }),
                            _cache[11] || (_cache[11] = _createElementVNode("span", null, "Recording is being processed...", -1))
                          ])
                        ]))
                      : (
            __props.callSid &&
            !(__props.recordingSid || callData.value?.recordingSid) &&
            (!__props.activityLog?.callLog ||
              __props.activityLog?.callLog?.status === 'completed' ||
              __props.activityLog?.callLog?.status === 'failed')
          )
                        ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                            _createElementVNode("div", _hoisted_26, [
                              _createVNode(_unref(Icon), {
                                icon: "mdi:information-outline",
                                width: "18",
                                height: "18"
                              }),
                              _createElementVNode("span", null, _toDisplayString(!__props.activityLog?.callLog
                  ? "Call was initiated but not completed"
                  : "Recording is not available for this call"), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true),
                (isLoadingCallData.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                      _createVNode(_unref(LoadingBars), { size: "sm" })
                    ]))
                  : (error.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
                        _createElementVNode("p", _hoisted_29, _toDisplayString(error.value), 1),
                        _createElementVNode("button", {
                          type: "button",
                          class: "btn btn-primary btn-sm",
                          onClick: _cache[6] || (_cache[6] = () => _unref(refetch)())
                        }, " Retry ")
                      ]))
                    : (_openBlock(), _createBlock(CallAnalysisContent, {
                        key: 6,
                        "call-data": callData.value,
                        "is-loading": false,
                        onRegenerate: handleRegenerate,
                        onSendFollowUpEmail: handleFollowUpEmail,
                        onRefreshTranscript: handleRefreshTranscript
                      }, null, 8, ["call-data"]))
              ], 64))
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})